// Local File / Asset Library support for the full app (index.html, served at /lite).
//
// The desktop app's upload flow is: POST /api/assets/upload → POST /api/transcribe/:id
// → poll /api/transcribe/status/:jobId (or /api/assets until transcriptionPath shows
// up) → GET the transcript → POST /api/analyze → /api/video/process. The web shim only
// had the upload route: /api/transcribe/* soft-404'd and /api/assets always returned
// [], so the UI sat on "Waiting for transcript…" for its full 2-hour timeout and Local
// File never worked. This module supplies the missing pieces, client-side.
//
// Pure logic with injected dependencies (store + transcriber + analyzers) so Node tests
// can drive it; src/shim/routes.js wires the real ones.

const uid = () => (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.floor(Math.random() * 1e6)}`).slice(0, 18);

/**
 * @param {object} deps
 * @param {(path:string, blob:Blob, type?:string)=>Promise} deps.putBlob
 * @param {(path:string)=>Promise<Blob|null>} deps.getBlob
 * @param {(blob:Blob, opts:{language:string, onProgress:(p:number)=>void})=>Promise<{text:string, words:Array, segments:Array}>} deps.transcribe
 */
export function createAssetService({ putBlob, getBlob, transcribe }) {
  const assets = new Map();      // id -> asset (the shape the desktop /api/assets returns)
  const transcripts = new Map(); // id -> { text, words, segments }
  const jobs = new Map();        // jobId -> { status, progress, logs, assetId, error }

  async function upload(file, { name } = {}) {
    if (!file) throw new Error('no file');
    const id = uid();
    const stored = `${id}-${file.name || 'video.mp4'}`;
    const path = `uploads/${stored}`;
    await putBlob('/' + path, file, file.type || 'video/mp4');
    const asset = {
      id,
      path,                       // what /api/video/process + /api/analyze get as videoPath/url
      filename: stored,           // the UI previews `/uploads/${filename}` (SW serves it)
      name: name || file.name || 'video.mp4',
      size: file.size || 0,
      type: 'asset',
      createdAt: new Date().toISOString(),
      transcriptionPath: null,    // set when transcription completes
      transcriptionError: null,   // set when it fails, so pollers stop waiting
    };
    assets.set(id, asset);
    return asset;
  }

  function list() {
    return [...assets.values()].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }

  function byPath(p) {
    const clean = String(p || '').replace(/^\/+/, '');
    return list().find((a) => a.path === clean) || null;
  }

  function startTranscription(assetId, { language = 'id' } = {}) {
    const asset = assets.get(assetId);
    if (!asset) return null;
    const jobId = uid();
    const job = { status: 'running', progress: 0, logs: ['[INFO] Menyiapkan audio…'], assetId, error: null };
    jobs.set(jobId, job);
    (async () => {
      try {
        const blob = await getBlob('/' + asset.path);
        if (!blob) throw new Error('File upload tidak ditemukan di browser ini. Upload ulang.');
        job.logs.push('[STEP] Transkrip audio…');
        const tr = await transcribe(blob, {
          language,
          onProgress: (p) => { job.progress = Math.max(job.progress, Math.min(99, Math.round(p * 100))); },
        });
        transcripts.set(asset.id, { text: tr.text || '', words: tr.words || [], segments: tr.segments || [] });
        asset.transcriptionPath = `api/transcripts/${asset.id}`; // App GETs `/${transcriptionPath}` → shim route
        job.progress = 100;
        job.status = 'completed';
        job.logs.push(`[DONE] Transkrip selesai (${(tr.segments || []).length} segmen).`);
      } catch (e) {
        job.status = 'failed';
        job.error = e?.message || String(e);
        asset.transcriptionError = job.error;
        job.logs.push(`[ERROR] Transkrip gagal: ${job.error}`);
      }
    })();
    return jobId;
  }

  const getJob = (jobId) => jobs.get(jobId) || null;
  const getTranscript = (assetId) => transcripts.get(assetId) || null;

  // Word timings for karaoke subtitles at render time (runRenderJob looks here for an
  // uploaded videoPath, since uploads don't live under /clips/<analysisId>/).
  function wordsForPath(videoPath) {
    const a = byPath(videoPath);
    return (a && transcripts.get(a.id)?.words) || null;
  }

  return { upload, list, byPath, startTranscription, getJob, getTranscript, wordsForPath };
}

/**
 * POST /api/analyze for an uploaded asset. Prefers the stored, timestamped segments
 * over the flattened `transcript` text the UI sends.
 * @param {object} body - { url, transcript, instruction, model, llm, advanced, assetMetadata, language }
 * @param {object} deps - { assetService, analyzeViaLlm, analyzeForClips, geminiKey, geminiVariant }
 */
export async function analyzeAsset(body = {}, { assetService, analyzeViaLlm, analyzeForClips, geminiKey, geminiVariant } = {}) {
  const asset = assetService.byPath(body.url);
  const stored = asset ? assetService.getTranscript(asset.id) : null;
  const segments = stored?.segments?.length
    ? stored.segments
    : (body.transcript ? [{ start: 0, text: String(body.transcript) }] : []);
  const language = body.language === 'en' ? 'en' : 'id';
  const title = body.assetMetadata?.title || asset?.name || 'Qlipper Web';
  const numCuts = body.advanced?.numCuts;
  const maxDuration = body.advanced?.maxDuration;
  if (body.llm) {
    const r = await analyzeViaLlm({ llm: body.llm, segments, language, maxDuration, numCuts, instruction: body.instruction });
    return { ...r, source_info: { video_title: title } };
  }
  return analyzeForClips({
    segments, customPrompt: body.instruction, numCuts, maxDuration, language,
    videoTitle: title, apiKey: geminiKey, model: geminiVariant,
  });
}
